<template>
  <div class="confirm-purchase">
    <div v-if="$listeners.back" class="px-2 pt-2 mb-n2">
      <v-button class="mr-1" secondary round @click="() => $emit('back')">
        <v-icon icon="arrow_back" />
      </v-button>
    </div>

    <transition
      :name="
        transitionDirection === DIRECTIONS.FORWARD
          ? 'fade-out-left'
          : 'fade-out-right'
      "
      mode="out-in"
    >
      <div v-if="isConfirming" class="mt-3">
        <placeholder-rect full-width height="200px" class="m-0" />
      </div>

      <order-summary
        v-else
        v-model="purchaseEmail"
        :is-loading="isLoading"
        :product="product"
        :order="order"
        :readonly="hasConfirmed"
        :email-exists="emailExists"
      >
        <template v-if="product.has_payment_plan">
          <list-item class="confirm-purchase__split-payment">
            <v-switch
              v-if="order"
              :value="asPaymentPlan"
              @input="togglePaymentPlan"
              class="d-flex align-items-center"
            >
              {{
                $t("components.checkout.confirmPurchase.splitIntoPaymentsOf", {
                  n: splitPaymentsCount
                })
              }}
              {{ order.initial.symbol }}{{ formatAmount(splitPaymentAmount) }}
              {{ order.initial.currency }}
            </v-switch>
          </list-item>
        </template>

        <template v-if="!useIAP && order">
          <order-discount
            v-if="isDiscountApplied(order)"
            :product="product"
            :order="order"
            @remove="attemptRemoveCoupon"
          />

          <template v-else>
            <list-item
              v-if="!isElementHidden(HIDDEN_ELEMENTS.COUPON)"
              no-padding-top
            >
              <div
                class="v-list-item__title pt-2 confirm-purchase__coupon-wrapper"
              >
                <div v-if="showCouponForm">
                  <div class="d-flex align-items-center">
                    <v-input
                      v-model="$v.couponCode.$model"
                      data-cy="coupon-input"
                      class="flex-grow-1"
                      validate-on-init
                      autofocus
                      :invalid-message="couponInvalidMessage"
                      :placeholder="
                        $t(
                          'components.checkout.confirmPurchase.placeholderCouponForm'
                        )
                      "
                      :validator="$v.couponCode"
                    />
                    <v-button
                      class="ml-1"
                      round
                      ghost
                      @click="() => hideCouponForm()"
                    >
                      <v-icon class="mr-0" icon="close"></v-icon>
                    </v-button>
                  </div>

                  <v-button
                    primary
                    small
                    block
                    data-role="apply-coupon"
                    data-cy="apply-coupon"
                    :disabled="!couponCode"
                    :is-loading="isFetchingCoupon"
                    class="mt-1"
                    @click="() => previewProductOrder()"
                  >
                    {{ $t("components.checkout.confirmPurchase.apply") }}
                  </v-button>
                </div>
                <template v-else>
                  <v-button
                    key="add-coupon-button"
                    class="d-flex align-items-center add-coupon-button pl-0 py-0"
                    primary
                    small
                    ghost
                    :disabled="asPaymentPlan"
                    @click="showCouponForm = true"
                  >
                    <v-icon icon="sell" />{{
                      $t("components.checkout.confirmPurchase.applyCoupon")
                    }}
                  </v-button>
                </template>
              </div>
            </list-item>
          </template>
        </template>

        <template v-slot:after>
          <order-bumps
            v-if="isPresent(orderBumps) && !useIAP"
            :selected-order-bumps="selectedOrderBumps"
            :order-bumps="orderBumps"
            :is-loading="isLoading"
            @update="handleOrderBumpsUpdate"
          />

          <div v-if="order" class="d-flex align-items-center mt-3 px-2">
            <template v-if="useIAP">
              <confirm-purchase-iap
                v-if="hasConfirmed"
                class="w-100"
                :product-id="product.id"
                :email="purchaseEmail"
                @success="args => $emit('success', args)"
                @cancel="() => (hasConfirmed = false)"
                @error="handlePaymentError"
              />

              <v-button
                v-else
                primary
                block
                :disabled="!canContinue"
                :is-loading="isLoading"
                @click="handleConfirm"
              >
                {{
                  hasTrial && order.initial.final_amount_cents === 0
                    ? $t(
                        "components.checkout.confirmPurchase.continueFreeTrial"
                      )
                    : $t("components.checkout.confirmPurchase.continuePayment")
                }}
              </v-button>
            </template>
            <template v-else-if="isReadyToMountPayment">
              <div class="confirm-purchase__payment-element w-100">
                <payment-element
                  v-model="purchaseEmail"
                  :product-id="product.id"
                  :product-version-id="productVersionId"
                  :is-subscription="isSubscription"
                  :coupon-code="couponCode"
                  :has-trial="hasTrial"
                  :amount="order.initial.final_amount"
                  :amount-cents="order.initial.final_amount_cents"
                  :currency="order.initial.currency"
                  :symbol="order.initial.symbol"
                  :is-loading-info="isLoading || isCheckingEmail"
                  :order-bumps="selectedOrderBumps"
                  :email-exists="emailExists"
                  @error="handlePaymentError"
                  @change="handleUserDetailsChange"
                  @refresh="refreshProductAndPreview"
                />
              </div>
            </template>
          </div>
        </template>
      </order-summary>
    </transition>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import { validationMixin } from "vuelidate"
import { email } from "vuelidate/lib/validators"

import { ACTIVITY_TYPES } from "@/store/analytics"

import debounce from "@/lib/debounce"
import { isPresent } from "@/lib/utils"
import { toDate, add } from "@/lib/date-utils"
import {
  shouldUseIAPForProduct,
  isDiscountApplied,
  formatAmount,
  ERROR_REASONS
} from "@/lib/checkout-utils"
import { HIDDEN_ELEMENTS } from "@/lib/constants"

import * as LocalStorage from "@/lib/local-storage"

import ListItem from "@/components/ListItem"
import vInput from "@/components/forms/vInput"
import PaymentElement from "@/components/checkout/PaymentElement"
import PlaceholderRect from "@/components/PlaceholderRect"
import OrderSummary from "@/components/checkout/OrderSummary"
import { STAGES as AUTH_STAGES } from "@/components/AuthModal"

const DIRECTIONS = {
  FORWARD: "forward",
  BACKWARD: "backward"
}

export default {
  components: {
    ListItem,
    vInput,
    PaymentElement,
    PlaceholderRect,
    OrderSummary,
    OrderBumps: () => import("@/components/checkout/OrderBumps"),
    OrderDiscount: () => import("@/components/checkout/OrderDiscount"),
    ConfirmPurchaseIap: () =>
      import("@/components/checkout/ConfirmPurchaseIap"),
    vSwitch: () => import("@/components/forms/vSwitch")
  },

  props: {
    product: { type: Object, required: true }
  },

  data() {
    return {
      purchaseEmail: "",
      emailExists: false,
      isCheckingEmail: false,
      isReadyToMountPayment: false,

      isLoading: false,
      isFetchingCoupon: false,

      couponCode: "",
      showCouponForm: false,
      couponInvalidMessage: "",
      address: null,

      asPaymentPlan: false,

      isConfirming: false,
      hasConfirmed: false,
      order: null,

      orderBumps: this.product.order_bumps || [],
      selectedOrderBumps: [],

      latestVersionId: this.product.version_id,
      paymentPlanVersionId: this.product.payment_plan_version_id,
      splitPaymentsCount: this.product.split_payments_count,

      transitionDirection: DIRECTIONS.FORWARD,

      DIRECTIONS,
      HIDDEN_ELEMENTS
    }
  },

  mixins: [validationMixin],

  mounted() {
    this.previewProductOrder()

    this.purchaseEmail = isPresent(this.currentUser)
      ? this.currentUser.email
      : LocalStorage.get(LocalStorage.LS_KEYS.LAST_EMAIL)

    this.isReadyToMountPayment = true
  },

  methods: {
    isPresent,
    toDate,
    add,
    debounce,
    isDiscountApplied,
    formatAmount,

    ...mapActions("products", ["previewOrder", "fetchProduct"]),
    ...mapActions("analytics", ["trackActivity"]),
    ...mapActions("auth", ["fetchUserAuth"]),

    openAuthModal() {
      this.$router.push(AUTH_STAGES.INITIAL, { append: true })
    },

    handlePaymentError(error) {
      this.$emit("error", error)
    },

    refreshProductAndPreview(limiter = 1) {
      this.fetchProduct(this.product.id).then(product => {
        this.orderBumps = product.order_bumps
        this.selectedOrderBumps = this.selectedOrderBumps.filter(ob =>
          this.orderBumps.find(b => b.id === ob.id)
        )

        this.latestVersionId = product.version_id
        this.paymentPlanVersionId = product.payment_plan_version_id
        this.splitPaymentsCount = product.split_payments_count

        this.previewProductOrder(this.selectedOrderBumps, limiter + 1)
      })
    },

    handleUserDetailsChange({ address }) {
      if (this.hasEnabledTaxCollection) {
        this.address = address

        this.previewProductOrder()
      } else {
        this.address = null
      }
    },

    previewProductOrder(orderBumps = this.selectedOrderBumps, limiter = 1) {
      this.isFetchingCoupon = true

      this.previewOrder({
        productId: this.product.id,
        versionId: this.productVersionId,
        couponCode: this.couponCode,
        orderBumps,
        address: this.address
      })
        .then(({ order, applied_order_bumps }) => {
          this.order = order
          this.selectedOrderBumps = applied_order_bumps

          this.showCouponForm = false
        })
        .catch(err => {
          if (err.response?.data?.message) {
            if (err.response.data.reason === ERROR_REASONS.invalid_coupon) {
              this.couponInvalidMessage = err.response.data.message
            } else {
              this.$notify(err.response.data.message)
            }

            if (
              [
                ERROR_REASONS.order_preview_mismatch,
                ERROR_REASONS.invalid_version
              ].includes(err.response?.data?.reason) &&
              limiter < 5
            ) {
              // prevent accidental infinite loop with limiter
              this.refreshProductAndPreview(limiter)
            }
          } else {
            throw err
          }
        })
        .finally(() => {
          this.isFetchingCoupon = false
        })
    },

    handleOrderBumpsUpdate(orderBumps) {
      this.previewProductOrder(orderBumps)
    },

    attemptRemoveCoupon() {
      this.couponCode = ""
      this.showCouponForm = false
      this.couponInvalidMessage = ""

      this.previewProductOrder()
    },

    togglePaymentPlan() {
      this.asPaymentPlan = !this.asPaymentPlan
      this.previewProductOrder()
    },

    handleConfirm() {
      this.transitionDirection = DIRECTIONS.FORWARD

      this.trackActivity({
        activityType: ACTIVITY_TYPES.CHECKOUT_PROGRESSED,
        data: {
          product: this.product,
          guest: this.isGuest,
          iap: this.useIAP
        }
      })

      this.hasConfirmed = true
    },

    handleBack() {
      this.hasConfirmed = false
      this.$emit("back")
    },

    handleEmailChange() {
      if (!this.isAuthenticated && this.isValidEmail) {
        this.emailExists = false
        this.isCheckingEmail = true

        // Ensure email is not changed while fetching
        const email = this.purchaseEmail

        this.fetchUserAuth({ email: this.purchaseEmail })
          .then(res => {
            if (this.purchaseEmail === email) {
              this.emailExists = res.exists && !res.is_passwordless_guest
            }
          })
          .finally(() => {
            this.isCheckingEmail = false
          })
      } else {
        this.emailExists = false
      }

      LocalStorage.set(LocalStorage.LS_KEYS.LAST_EMAIL, this.purchaseEmail)
    },

    hideCouponForm() {
      this.couponCode = ""
      this.showCouponForm = false
    }
  },

  watch: {
    couponCode(couponCode) {
      if (!couponCode) {
        this.couponInvalidMessage = ""
      }
    },

    isAuthenticated() {
      if (this.isAuthenticated) {
        this.purchaseEmail = this.currentUser.email
        this.emailExists = false
      }
    },

    purchaseEmail() {
      debounce(this.handleEmailChange, 200)
    }
  },

  computed: {
    ...mapGetters(["hasEnabledTaxCollection"]),
    ...mapGetters("auth", ["currentUser", "isGuest", "isAuthenticated"]),
    ...mapGetters("ui", ["isElementHidden"]),

    canSubmitEmail() {
      return (
        isPresent(this.currentUser) ||
        (this.isValidEmail && !this.emailExists && !this.isCheckingEmail)
      )
    },

    useIAP() {
      return shouldUseIAPForProduct(this.product)
    },

    isValidEmail() {
      return isPresent(this.purchaseEmail) && email(this.purchaseEmail)
    },

    canContinue() {
      return !this.isLoading && this.canSubmitEmail
    },

    isSubscription() {
      return isPresent(this.order?.upcoming)
    },

    hasTrial() {
      if (!this.order) return false

      return this.order.initial.line_items[0].trial_period_days > 0
    },

    splitPaymentAmount() {
      return (
        (this.order?.initial?.line_items[0].amount || 0) /
        (this.splitPaymentsCount || 1)
      )
    },

    productVersionId() {
      return this.asPaymentPlan
        ? this.paymentPlanVersionId
        : this.latestVersionId
    }
  },

  validations: {
    couponCode: {}
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.confirm-purchase {
  --primary-brand-color-hex: #000;
  --primary-brand-color-rgb-vals: 0, 0, 0;
  --button-text-color-hex: #fff;
}

.confirm-purchase__tax-loading {
  .v-loader {
    width: $size--5;
    height: $size--5;
  }
}

.confirm-purchase__coupon-wrapper {
  .v-icon {
    position: relative;
    top: -1px;
    font-size: $font-size--small;
    color: var(--primary-brand-color-hex);
  }
}

.v-list-item.confirm-purchase__split-payment {
  background: $color-ui--grey-10;

  margin: 0.5rem 0.5rem 0;
  border-radius: var(--border-radius);
  border-bottom: 0;

  padding-bottom: $size--3;

  .v-list-item__row {
    padding: $size--3;
    padding-bottom: 0;
  }

  &:hover {
    background: $color-ui--grey-10;
  }

  .v-switch__label {
    margin-left: $size--3;
    font-size: 0.925rem;
    font-weight: 500;
  }
}
</style>
